import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, DocumentTextIcon, PencilIcon, TrashIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';
import { downloadReceiptPdf, printReceiptWindow } from '../../utils/receipt';
import { useAuth } from '../../contexts/AuthContext';

const Expenses = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'fuel',
    receiptNumber: '',
    vendor: '',
    person: '',
    vehicleUsed: '',
    notes: ''
  });
  const [statementFilters, setStatementFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    status: '',
    person: '',
    vehicleUsed: ''
  });
  const [statementExpenses, setStatementExpenses] = useState([]);
  const [statementTotal, setStatementTotal] = useState(0);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementError, setStatementError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    fetchExpenses();
  }, [page, limit, filters]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get('/api/expenses', {
        params: {
          page,
          limit,
          search: filters.search || undefined,
          category: filters.category || undefined,
          status: filters.status || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined
        }
      });
      setExpenses(response.data.expenses || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const openModal = (expense = null) => {
    setError('');
    setEditingExpense(expense);
    if (expense) {
      setFormData({
        description: expense.description || '',
        amount: expense.amount || '',
        category: expense.category || 'fuel',
        receiptNumber: expense.receiptNumber || '',
        vendor: expense.vendor || '',
        person: expense.person || '',
        vehicleUsed: expense.vehicleUsed || '',
        notes: expense.notes || ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setFormData({
      description: '',
      amount: '',
      category: 'fuel',
      receiptNumber: '',
      vendor: '',
      person: '',
      vehicleUsed: '',
      notes: ''
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'category' && value !== 'vehicle') {
        next.person = '';
        next.vehicleUsed = '';
      }
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        description: formData.description,
        amount: Number(formData.amount),
        category: formData.category,
        receiptNumber: formData.receiptNumber || undefined,
        vendor: formData.vendor || undefined,
        person: formData.category === 'vehicle' ? formData.person || undefined : undefined,
        vehicleUsed: formData.category === 'vehicle' ? formData.vehicleUsed || undefined : undefined,
        notes: formData.notes || undefined
      };

      if (editingExpense) {
        await axios.put(`/api/expenses/${editingExpense._id}`, payload);
      } else {
        await axios.post('/api/expenses', payload);
      }

      closeModal();
      fetchExpenses();
    } catch (submitError) {
      console.error('Error saving expense:', submitError);
      setError(
        submitError.response?.data?.message || 'Failed to save expense. Try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`/api/expenses/${expenseId}`);
      fetchExpenses();
    } catch (deleteError) {
      console.error('Error deleting expense:', deleteError);
    }
  };

  const handleApprove = async (expenseId) => {
    if (!window.confirm('Approve this expense?')) return;
    setActionLoadingId(expenseId);
    setActionError('');
    try {
      await axios.put(`/api/expenses/${expenseId}/approve`);
      fetchExpenses();
    } catch (actionErr) {
      console.error('Error approving expense:', actionErr);
      setActionError(actionErr.response?.data?.message || 'Failed to approve expense.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (expenseId) => {
    if (!window.confirm('Reject this expense?')) return;
    setActionLoadingId(expenseId);
    setActionError('');
    try {
      await axios.put(`/api/expenses/${expenseId}/reject`);
      fetchExpenses();
    } catch (actionErr) {
      console.error('Error rejecting expense:', actionErr);
      setActionError(actionErr.response?.data?.message || 'Failed to reject expense.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleStatementChange = (event) => {
    const { name, value } = event.target;
    setStatementFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'category' && value !== 'vehicle') {
        next.person = '';
        next.vehicleUsed = '';
      }
      return next;
    });
  };

  const fetchStatement = async () => {
    setStatementLoading(true);
    setStatementError('');
    try {
      const params = {
        startDate: statementFilters.startDate || undefined,
        endDate: statementFilters.endDate || undefined,
        category: statementFilters.category || undefined,
        status: statementFilters.status || undefined,
        person: statementFilters.person || undefined,
        vehicleUsed: statementFilters.vehicleUsed || undefined
      };
      const response = await axios.get('/api/expenses/statement', { params });
      setStatementExpenses(response.data.expenses || []);
      setStatementTotal(response.data.totalAmount || 0);
    } catch (fetchError) {
      console.error('Error fetching statement:', fetchError);
      setStatementError('Failed to load expense statement.');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleStatementReceipt = (action) => {
    const columns = ['Date', 'Description', 'Category', 'Person', 'Vehicle', 'Amount'];
    const rows = statementExpenses.map((expense) => [
      new Date(expense.createdAt).toLocaleDateString(),
      expense.description,
      expense.category,
      expense.person || '',
      expense.vehicleUsed || '',
      formatCurrency(expense.amount)
    ]);
    rows.push(['', '', '', '', 'Total', formatCurrency(statementTotal)]);

    const metaLines = [
      `From: ${statementFilters.startDate || 'All'}`,
      `To: ${statementFilters.endDate || 'All'}`,
      statementFilters.category ? `Category: ${statementFilters.category}` : null,
      statementFilters.status ? `Status: ${statementFilters.status}` : null,
      statementFilters.person ? `Person: ${statementFilters.person}` : null,
      statementFilters.vehicleUsed ? `Vehicle: ${statementFilters.vehicleUsed}` : null
    ].filter(Boolean);

    if (action === 'print') {
      printReceiptWindow({
        title: 'Expense Statement',
        metaLines,
        columns,
        rows
      });
    } else {
      downloadReceiptPdf({
        title: 'Expense Statement',
        metaLines,
        columns,
        rows,
        filename: 'expense-statement.pdf'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Expenses</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Track and manage business expenses.
          </p>
        </div>
        {(user?.role === 'admin' || user?.role === 'finance') && (
          <button className="btn-primary flex items-center gap-2" onClick={() => openModal()}>
            <PlusIcon className="h-5 w-5" />
            Add Expense
          </button>
        )}
      </div>

      {/* Expense Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <DocumentTextIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Expenses</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(0)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-100">
                <DocumentTextIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Pending Approval</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(0)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                <DocumentTextIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Approved</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(0)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                <DocumentTextIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">This Month</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search expenses..."
              className="input-field md:col-span-2"
            />
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All categories</option>
              <option value="fuel">Fuel</option>
              <option value="repairs">Repairs</option>
              <option value="salaries">Salaries</option>
              <option value="utilities">Utilities</option>
              <option value="transport">Transport</option>
              <option value="maintenance">Maintenance</option>
              <option value="vehicle">Vehicle</option>
              <option value="other">Other</option>
            </select>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="card bg-secondary-50">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Person / Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Recorded By
                </th>
                {user?.role === 'admin' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {expenses.map((expense) => (
                <tr key={expense._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-secondary-900">
                      {expense.description}
                    </div>
                    {expense.vendor && (
                      <div className="text-sm text-secondary-500">
                        {expense.vendor}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {formatCurrency(expense.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {expense.category === 'vehicle' ? (
                      <>
                        <div className="text-sm text-secondary-900">{expense.person || '-'}</div>
                        <div className="text-xs text-secondary-500">{expense.vehicleUsed || ''}</div>
                      </>
                    ) : (
                      <div className="text-sm text-secondary-500">-</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {new Date(expense.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      expense.status === 'approved' 
                        ? 'bg-success-100 text-success-800'
                        : expense.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {expense.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {expense.recordedBy?.name}
                  </td>
                  {user?.role === 'admin' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {expense.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(expense._id)}
                              className="text-success-600 hover:text-success-700"
                              disabled={actionLoadingId === expense._id}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(expense._id)}
                              className="text-red-600 hover:text-red-700"
                              disabled={actionLoadingId === expense._id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openModal(expense)}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {actionError && (
          <div className="px-6 pb-4 text-sm text-red-600">
            {actionError}
          </div>
        )}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
          <div>
            Showing page {page} of {totalPages} · {total} expenses
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              className="input-field h-9"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Expense Statement */}
      <div className="card bg-secondary-50">
        <div className="card-header">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-secondary-900">Expense Statement</h2>
              <p className="text-sm text-secondary-600">Filter expenses and export a statement.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => handleStatementReceipt('print')}
                disabled={statementExpenses.length === 0}
              >
                <PrinterIcon className="h-4 w-4" />
                Print
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => handleStatementReceipt('pdf')}
                disabled={statementExpenses.length === 0}
              >
                <DocumentTextIcon className="h-4 w-4" />
                PDF
              </button>
            </div>
          </div>
        </div>
        <div className="card-body space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-secondary-700">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={statementFilters.startDate}
                onChange={handleStatementChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">End Date</label>
              <input
                type="date"
                name="endDate"
                value={statementFilters.endDate}
                onChange={handleStatementChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">Category</label>
              <select
                name="category"
                value={statementFilters.category}
                onChange={handleStatementChange}
                className="input-field"
              >
                <option value="">All</option>
                <option value="fuel">Fuel</option>
                <option value="repairs">Repairs</option>
                <option value="salaries">Salaries</option>
                <option value="utilities">Utilities</option>
                <option value="transport">Transport</option>
                <option value="maintenance">Maintenance</option>
                <option value="vehicle">Vehicle</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">Status</label>
              <select
                name="status"
                value={statementFilters.status}
                onChange={handleStatementChange}
                className="input-field"
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {statementFilters.category === 'vehicle' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Person</label>
                  <input
                    type="text"
                    name="person"
                    value={statementFilters.person}
                    onChange={handleStatementChange}
                    className="input-field"
                    placeholder="Search person"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Vehicle Used</label>
                  <input
                    type="text"
                    name="vehicleUsed"
                    value={statementFilters.vehicleUsed}
                    onChange={handleStatementChange}
                    className="input-field"
                    placeholder="Search vehicle"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button className="btn-primary" onClick={fetchStatement} disabled={statementLoading}>
              {statementLoading ? 'Loading...' : 'Generate Statement'}
            </button>
            <div className="text-sm text-secondary-600">
              Total: <span className="font-semibold text-secondary-900">{formatCurrency(statementTotal)}</span>
            </div>
          </div>
          {statementError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {statementError}
            </div>
          )}
          {statementExpenses.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Person</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Vehicle</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {statementExpenses.map((expense) => (
                    <tr key={expense._id}>
                      <td className="px-4 py-2 text-sm text-secondary-900">
                        {new Date(expense.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2 text-sm text-secondary-900">{expense.description}</td>
                      <td className="px-4 py-2 text-sm text-secondary-900">{expense.category}</td>
                      <td className="px-4 py-2 text-sm text-secondary-900">{expense.person || '-'}</td>
                      <td className="px-4 py-2 text-sm text-secondary-900">{expense.vehicleUsed || '-'}</td>
                      <td className="px-4 py-2 text-sm text-secondary-900">{formatCurrency(expense.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className="input-field"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="fuel">Fuel</option>
                    <option value="repairs">Repairs</option>
                    <option value="salaries">Salaries</option>
                    <option value="utilities">Utilities</option>
                    <option value="transport">Transport</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="vehicle">Vehicle</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Receipt Number</label>
                  <input
                    type="text"
                    name="receiptNumber"
                    value={formData.receiptNumber}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Vendor</label>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
                {formData.category === 'vehicle' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Person</label>
                      <input
                        type="text"
                        name="person"
                        value={formData.person}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="e.g. Driver name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">Vehicle Used</label>
                      <input
                        type="text"
                        name="vehicleUsed"
                        value={formData.vehicleUsed}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="e.g. KAA 123A"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="input-field"
                    rows="3"
                  />
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Expense'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
